/**
 * 首页「近期动态」（运营主管设计 T4 / D10）：owner 放权给运营主管之后的「眼睛」。
 *
 * 近 7 天三类会扩大风险面的动作：新增了哪些销售账号、转交了哪些客户、新增了哪些合伙人。
 * 仅超管（持 advisor:manage）渲染并请求接口；运营主管 / 销售 / 其它角色既看不到、也不会发请求。
 *
 * 每张卡独立降级：后端某一块没读到时，对应卡片显示一行灰字，其余照常；整个请求失败也只是面板里一行灰字，不拖垮首页。
 * 客户只用「N 位」表达——接口里没有客户称呼、手机号、微信号。
 *
 * 扩展位：children 渲染在三张卡下面（同一个面板容器内）。后面「待审批发券」卡（home/voucher-approvals.tsx）
 * 可以直接挂在这里，也可以作为 <OversightPanel /> 的兄弟节点挂在 home/index.tsx；本文件不实现它。
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, Col, Empty, List, Row, Space, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../contracts/permissions';
import { roleLabel } from '../../utils/role-labels';
import {
  getOversight,
  type OversightPartnerDto,
  type OversightSalesAccountDto,
  type OversightTransferDto,
} from '../../api/oversight';

const { Text } = Typography;

const PARTNER_STATUS: Record<OversightPartnerDto['status'], { color: string; label: string }> = {
  pending_bind: { color: 'orange', label: '待认领' },
  active: { color: 'green', label: '合作中' },
  stopped: { color: 'default', label: '已停止' },
};

const PARTIAL_HINT = '这部分数据暂时没取到';

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 毫秒时间戳 → 「10-15 12:00」，固定按 UTC+8 显示（与运营所在时区一致，不受浏览器时区影响） */
export function formatMonthDayTime(ms: number): string {
  const d = new Date(ms + 8 * 60 * 60 * 1000);
  return `${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

function SalesAccountRow({ item }: { item: OversightSalesAccountDto }) {
  const creator = item.createdBy ? item.createdBy.nickname ?? item.createdBy.id : '未知';
  return (
    <List.Item data-testid="oversight-sales-row">
      <Space direction="vertical" size={0}>
        <Space size={8}>
          <Text strong>{item.nickname}</Text>
          {item.status === 'active' ? <Tag color="green">在职</Tag> : <Tag color="red">已停用</Tag>}
        </Space>
        <Text type="secondary">
          {creator} 创建 · {formatMonthDayTime(item.createdAt)}
        </Text>
      </Space>
    </List.Item>
  );
}

function TransferRow({ item }: { item: OversightTransferDto }) {
  const actor = item.actor.nickname ?? item.actor.id;
  const to = item.to.nickname ?? item.to.id;
  return (
    <List.Item data-testid="oversight-transfer-row" data-flagged={item.flagged ? '1' : '0'}>
      <Space direction="vertical" size={2}>
        <Text>
          {`${actor} → ${to}：${item.customerCount} 位客户`}
          {item.reasons.length > 0 && <Text type="secondary">{` · ${item.reasons.join('；')}`}</Text>}
        </Text>
        <Space size={8} wrap>
          {item.flagged && <Tag color="red">转交较多，请核对</Tag>}
          <Text type="secondary">
            {roleLabel(item.actor.role)}操作 · 最近 {formatMonthDayTime(item.lastAt)}
          </Text>
        </Space>
      </Space>
    </List.Item>
  );
}

function PartnerRow({ item }: { item: OversightPartnerDto }) {
  const meta = PARTNER_STATUS[item.status] ?? { color: 'default', label: item.status };
  return (
    <List.Item data-testid="oversight-partner-row">
      <Space direction="vertical" size={0}>
        <Space size={8}>
          <Text strong>{item.name}</Text>
          <Tag color={meta.color}>{meta.label}</Tag>
        </Space>
        <Text type="secondary">
          {item.city ?? '城市未填'} · {formatMonthDayTime(item.createdAt)}
        </Text>
      </Space>
    </List.Item>
  );
}

interface DigestCardProps<T> {
  title: string;
  link: { to: string; label: string };
  items: T[];
  partial: boolean;
  emptyText: string;
  testId: string;
  renderItem: (item: T) => ReactNode;
}

function DigestCard<T>({ title, link, items, partial, emptyText, testId, renderItem }: DigestCardProps<T>) {
  return (
    <Card title={title} size="small" extra={<Link to={link.to}>{link.label}</Link>} data-testid={testId} style={{ height: '100%' }}>
      {items.length > 0 ? (
        <List size="small" dataSource={items} renderItem={(item) => renderItem(item)} />
      ) : (
        // 没取到时不能说「没有新增」：空数组可能只是读失败
        !partial && <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
      {partial && (
        <Text type="secondary" style={{ display: 'block', marginTop: items.length > 0 ? 8 : 0 }}>
          {PARTIAL_HINT}
        </Text>
      )}
    </Card>
  );
}

export function OversightPanel({ children }: { children?: ReactNode }) {
  const canManage = usePermission(PERMISSIONS.ADVISOR_MANAGE);
  const query = useQuery({
    queryKey: ['advisor-oversight'],
    queryFn: getOversight,
    enabled: canManage,
    retry: false,
  });
  if (!canManage) return null;

  const data = query.data;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', marginTop: 16 }} data-testid="oversight-panel">
      {query.isError || (!query.isLoading && !data) ? (
        <Card title="近期动态（近 7 天）" size="small">
          <Text type="secondary">近期动态暂时加载失败，可以直接进「销售账号 / 销售管理 / 合伙人」页查看。</Text>
        </Card>
      ) : query.isLoading || !data ? (
        <Card title="近期动态（近 7 天）" size="small" loading />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <DigestCard
              title="近 7 天新增销售"
              link={{ to: '/advisor/sales-accounts', label: '销售账号' }}
              items={data.newSalesAccounts}
              partial={data.partial.newSalesAccounts}
              emptyText="近 7 天没有新增销售"
              testId="oversight-sales"
              renderItem={(item) => <SalesAccountRow key={item.id} item={item} />}
            />
          </Col>
          <Col xs={24} lg={8}>
            <DigestCard
              title="近 7 天转交"
              link={{ to: '/advisor/sales', label: '销售管理' }}
              items={data.transfers}
              partial={data.partial.transfers}
              emptyText="近 7 天没有转交"
              testId="oversight-transfers"
              renderItem={(item) => <TransferRow key={`${item.actor.id}:${item.to.id}`} item={item} />}
            />
          </Col>
          <Col xs={24} lg={8}>
            <DigestCard
              title="近 7 天新增合伙人"
              link={{ to: '/partners', label: '合伙人' }}
              items={data.newPartners}
              partial={data.partial.newPartners}
              emptyText="近 7 天没有新增合伙人"
              testId="oversight-partners"
              renderItem={(item) => <PartnerRow key={item.id} item={item} />}
            />
          </Col>
        </Row>
      )}
      {children}
    </Space>
  );
}
